import {RefObject, useEffect, useRef, useState} from 'react';
import Dropdown from  '@/components/elements/global/Dropdown';
import useHTTP from  '@/components/hooks/useHTTP';

//components
import SimpleBar from 'simplebar-react';
import ModalWrapper from  '@/components/modals/ModalWrapper';
import ExistingItemModel from '../modals/ExistingItemModal';
import Aside from  '@/components/elements/archive/Aside';
import { CloseButton, ISelectButton, LoadingSpinner, SelectButtonList } from 'thrive-components';
import CaretButton from  '@/components/elements/global/buttons/CaretButton';
import { useOnClickOutside } from  '@/components/hooks/useClickOutside';

//helpers
import { debounce } from "lodash";
import { BuildPath } from  '@/helpers/Paths';
import { contentTypeImage } from  '@/helpers/Archive';
import { endOfDayTime, getDateRange, removeTime } from  '@/helpers/Dates';

//interfaces
import { IExistingItem, IOption } from  '@/interfaces/insightsp2';
import { archiveItem, archiveSortLabels, contentSortLabels, currApp, eStatus, FILTER_OPTIONS, TIME_VALUES, timeSortLabels } from  '@/interfaces/archive';

//styling
import '../../css/archive.scss'
  


const Archive = () => {
    const dropdownRef = useRef<any>(undefined)
    const [showDropdown, setShowDropdown] = useState<boolean>(false);
    const [contentType, setContentType] = useState<number | null>(-1);
    const [time, setTime] = useState<string>("allTime");
    const [sortBy, setSortBy] = useState<number>(0);
    const [keyword, setKeyword] = useState<string>("");
    const [totalItemCount, setTotalItemCount] = useState<number>(0);
    const [searchClicked, setSearchClicked] = useState<boolean>(false);
    const [searchDisabled, setSearchDisabled] = useState<boolean>(true);

    const [results, setResults] = useState<archiveItem[]>([]);
    const [selectedItem, setSelectedItem] = useState<archiveItem>();
    const [loading, setLoading] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [newLocation, setNewLocation] = useState<IExistingItem | null>(null);
    const [selectedWorkSpace, setSelectedWorkSpace] = useState<IOption | null>(null);
    const [selectNewLocation, setSelectNewLocation] = useState<boolean>(false);
    const [draft, setDraft] = useState<boolean>(false);
    const [status, setStatus] = useState<eStatus>(eStatus.noItem);
    const [app, setApp] = useState<currApp | null>(null);

    const {get, post} = useHTTP();
    const takeCount = 10;
    const searchRef = useRef<HTMLInputElement>(undefined);
    const appRef = useRef<currApp>(null);
    const endScrollRef = useRef<boolean>(false);
    const skipRef = useRef<number>(0);
    const initialisingRef = useRef(true);

    const filterByOptions: IOption[] = FILTER_OPTIONS.map(opt => ({
        value: opt.value,
        label: contentSortLabels[opt.index],
    }));

    const allTimes: IOption[] = TIME_VALUES.map((value, i) => ({
        value,
        label: timeSortLabels[i],
    }));

    const sortByDropdown: ISelectButton[] = archiveSortLabels.map((label, index) => (
        {
            content: label,
            onClick: () => handleSortBy(index),
            isSelected: sortBy === index,
        }
    ));

    // Get app details from .net / dom
    useEffect(() => {

        const targetContainer = document.getElementById('react-archive-container');
    
        if (targetContainer){
            const appId : string = targetContainer.getAttribute('data-appId') as string;
            const appGuid: string = targetContainer.getAttribute('data-appGuid') as string;

            setApp({appId, appGuid})

            appRef.current = {appId, appGuid}

            setSelectedWorkSpace({value:appGuid, label:''})

            console.log("use effect calls")
            search(true)
        }

    }, [])

    // Reset on upload
    useEffect(() => {

        if (status === eStatus.uploading && selectedItem){
            // Remove from list
            setResults(results => [...results].filter(x => x.id !== selectedItem.id));
            resetChecks()       
        }

    }, [status, selectedItem?.id])

    // Upload on new location selected
    useEffect(() => {

        if (newLocation){
            restore()
        }
    }, [newLocation])

    // Content type
    useEffect(() => {
        if (initialisingRef.current) {
            return;
        }

        console.log("content type calls search")

        search(true);
    }, [contentType]);

    // Time
    useEffect(() => {
        if (initialisingRef.current) {
            return;
        }
      console.log("time calls search")
        search(true);
    }, [time]);

    // Sort by
    useEffect(() => {
        if (initialisingRef.current) {
            return;
        }

        search(true, false, true);
    }, [sortBy]);

    useOnClickOutside(dropdownRef, () => setShowDropdown(false))

    // Enable - disable
    useEffect(() => {

        if (keyword.length){
            setSearchDisabled(false)
        }
        else{
            setSearchDisabled(true)
        }

    }, [keyword])

    function search(reset:boolean, clearKeyword?:boolean, sortReset?:boolean){

        setLoading(true);

        // If new search reset end scroll
        if (reset){
            endScrollRef.current = false;
            skipRef.current = 0;
            
            // Reset scroll
            const scroll = document.querySelector(".simplebar-content-wrapper");

            if (scroll ){
                scroll.scrollTop = 0;
            }
        }

        get(`content/archived?${queryBuilder(reset, sortReset)}`).then(({data}) => {

            const content : [] = data?.items ?? [];
            // No more items to come
            if(content.length < takeCount){
                endScrollRef.current = true
            }

            // New search
            if (reset){
                setResults([...content]);
            }
            // Append
            else{
                setResults((x) => [...x, ...content])
            }

            setTotalItemCount(data?.totalItemCount || 0)

        }).finally(() => {
            setLoading(false);    
            initialisingRef.current = false;
        }) 

        function queryBuilder(reset:boolean, sortReset?:boolean){

            let url = `skip=${reset ? 0 : skipRef.current}&SearchContentSortType=${sortBy}&appId=${String(appRef?.current?.appId)}`;

            // If sort by reset take current total to get all
            url += `&take=${sortReset ? results.length : takeCount}`

            if (keyword){
                url += `&searchTerm=${clearKeyword ? '' : keyword}`
            }

            if (contentType !== null && contentType !== -1){
                url += `&SearchContentFilterType=${contentType}`
            }

            if (time && time !== "allTime"){
                const {dateFrom, dateTo} = getDateRange(time);
                url +=  `&dateFrom=${removeTime(dateFrom).toISOString()}&dateTo=${endOfDayTime(dateTo).toISOString()}`;
            }

            return url;
        }
    }

    function resetChecks(){
        setSelectNewLocation(false)
        setDraft(false)
        setNewLocation(null)
    }

    function restore(){

        let locationId:number = 0 ;

        // Previous parent is available and new one hasnt been selected then use existing
        if (!selectNewLocation && !newLocation && selectedItem?.parentListIdPriorToArchiving){
            locationId = selectedItem.parentListIdPriorToArchiving
        }
        // Open modal first to pick location and exit
        else if (!newLocation){   
            setOpenModal(true)
            return
        }
        else if (newLocation?.id){
            locationId = newLocation.id
        }

        // Send
        const restore = {
            appId: parseInt(String(appRef?.current?.appId)),
            parentId:locationId,
            contentId: Number(selectedItem?.id),
            position: "",
            publish : !draft
        }     
     
        post(`content/restore`, restore).then( () => {
            setStatus(eStatus.uploading)
        }).finally(() => {

            setTotalItemCount(x => x - 1)

            // Call search again to have full list if more available
            search(true)
            resetChecks()
        }) 
  
    }

    function iconBuilder(item:archiveItem){

        if (item.featuredImageUrl){
            return <img className='featured' src={item.featuredImageUrl} alt="" />
        }

        return(
            <div className='content-type'>
                <img className='content-type' src={`${BuildPath()}${contentTypeImage(item.contentType)}` } alt="" />
            </div>
        )
    }

    function scrollHandler (event: any) {

        event.persist()

        debouncedScroll(event)
    }

    function handleCheckNewLocation(){
        setSelectNewLocation(!selectNewLocation)
    }

    function clear(){
        if (keyword){

            setSearchClicked(false)

            setKeyword("")

            searchRef?.current?.focus?.();

            search(true, true)
        }
    }

    function handleKeyword(e:any){

        if (e.target.value.length < 500){
            setKeyword(e.target.value)
        } 
    }

    function handleSearchClick(){

        search(true); 
        setSearchClicked(true);
        skipRef.current = 0;
        endScrollRef.current = false;
    }

    function handleSortBy(sort: number){

        const contentWrapper = document.querySelector(".simplebar-content-wrapper") as HTMLElement;

        if (contentWrapper){
            contentWrapper.scrollTop = 0;
        }
        setSortBy(sort)
    }
   
    const debouncedScroll = debounce((event) => {
    
        const {scrollHeight, scrollTop, clientHeight} = event.target;
        const isBottomReached = (scrollHeight - Math.floor(scrollTop) <= clientHeight + 15);

        if (isBottomReached) {
            skipRef.current += takeCount;

            if (!endScrollRef.current){
                console.log("end of scroll")
                search(false)
            }
        }

    }, 500)

    function handleSelectItemKeyDown(e: KeyboardEvent, item:archiveItem ){
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelectItem(item);
        }
    }

    function handleSelectItem(item:archiveItem) {
        setSelectedItem(item);
        setStatus(eStatus.selectedItem);
        resetChecks()
    }

    if (!selectedWorkSpace){
        return null
    }
    
    return (
        <div className='archive-container'>

            <h2 className='title'>Archive</h2>

            <div className='filters'>

                <Dropdown
                    className={'archive-content-type'}
                    options={filterByOptions}
                    defaultValue={"All Content"}
                    setValue={(opt) => setContentType(parseInt(opt as string))}
                    placeholder="All Content"
                />
                
                <Dropdown
                    className={'archive-times'}
                    options={allTimes}
                    defaultValue={"All Time"}
                    setValue={(opt) => setTime(opt as string)}
                    placeholder="All Time"
                />

                <div className='search-input-container'>
                    <label 
                        htmlFor='search-content-title-input' 
                        className='sr-only'
                    >
                        Search by Content title
                    </label>
                    
                    <input 
                        id='search-content-title-input' 
                        ref={searchRef as  RefObject<HTMLInputElement>}
                        type="text"
                        value={keyword} 
                        onKeyDown={(e) => e.key === "Enter" ? handleSearchClick() : null} 
                        onChange={handleKeyword} 
                        className='search-input' 
                        placeholder='Search by content title'
                    />
                    
                    {searchClicked && keyword && 
                        <CloseButton 
                            ariaLabel={'Clear Search'} 
                            onButtonClick={clear}
                        />
                    }
                </div>

                <button 
                    className='archive-btn search-btn' 
                    disabled={searchDisabled} 
                    onClick={handleSearchClick}
                >
                    Search
                </button>
            </div>

            <div className='results'>
                <div className={`results-controls ${!results.length && !loading ? "no-results-controls" : ""}`}>
                    <div className='item-count'>
                        {!loading && !!totalItemCount &&
                            <span>{totalItemCount} {totalItemCount === 1 ? 'Item' : 'Items'}</span>
                        }

                        {loading && 
                            <LoadingSpinner size={10} />
                        }
                    </div>

                    {!!results.length && 
                        <div className='sort-by-dropdown-wrapper'  ref={dropdownRef}>
                            <div className='sort-by-container'>
                                <span className="sort-by-text">Sort by: </span>

                                <CaretButton 
                                    content={archiveSortLabels[sortBy]} 
                                    ariaLabel={'Sort the content'} 
                                    onButtonClick={() => setShowDropdown(!showDropdown)}
                                />
                            </div>
                            
                            {showDropdown && 
                                <SelectButtonList
                                    buttons={sortByDropdown}
                                    className="sort-by-select-button-list" 
                                    allButtonOnClick={() => setShowDropdown(false)}
                                />
                            }
                        </div>
                    }
                </div>

                <div className='results-container'>
                    {(!loading || !!results.length) &&
                        <>
                        <div className="list">
                            {!!results.length 
                                ?   <SimpleBar scrollableNodeProps={{onScroll: (e:any) => scrollHandler(e)}} style={{ maxHeight: 600, height: 600  }}>
                                        <ul style={{height: 585, maxHeight:585}}>
                                            {results.map(item => {
                                                return (
                                                    <li
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() => handleSelectItem(item)}
                                                        onKeyDown={(event) => handleSelectItemKeyDown(event, item)}
                                                        key={item.id} 
                                                        className={item.id === selectedItem?.id ? 'selected' : ''}
                                                    >

                                                        <div className='icon'>
                                                            {iconBuilder(item)}
                                                        </div>

                                                        <div className='content'>
                                                            <h3>{item.title}</h3>

                                                            {item.firstPublishedAt 
                                                                ? <p>Originally Published by: {item.publishedByDisplayName ?? 'N/A'} on {new Date(item.firstPublishedAt).toLocaleDateString().replace(/\b0/g, '')}</p>
                                                                : <p>Draft Created by: {item.createdByDisplayName ?? 'N/A'} on {new Date(item.createdAt).toLocaleDateString().replace(/\b0/g, '')}</p>
                                                            }

                                                            <span>Archived by:{item.archivedByDisplayName ?? 'N/A'} on {new Date(item.archivedAt).toLocaleDateString().replace(/\b0/g, '')}</span>
                                                        </div>
                                                    </li>
                                                )
                                            })}
                                        </ul>
                                    </SimpleBar>
                                
                                :   <div className="no-results">
                                        <img src={`${BuildPath()}/images/unsplash/missing.svg`} alt="" />
                                        <h3>No items found</h3>
                                    </div>
                                }
                        </div>
                                
                        <aside>
                            <Aside 
                                status={status} 
                                selectedItem={selectedItem}
                                contentItem={newLocation as IExistingItem}
                                selectNewLocation={selectNewLocation} 
                                handleCheckNewLocation={handleCheckNewLocation}  
                                draft={draft}
                                setDraft={setDraft}
                                restore={restore} 
                                app={app as currApp}
                            />
                        </aside>
                    </>
                    }
                </div>
            </div>

            <ModalWrapper
                isOpen={openModal}
                onRequestClose={() => setOpenModal(false)}
                className="existing-content-model archive-existing-content-modal"
            >
                <ExistingItemModel 
                    setContentItem={setNewLocation}
                    close={() => setOpenModal(false)} 
                    selectedWorkSpace={selectedWorkSpace} 
                    buttonText='Restore' 
                    restoreItemId={selectedItem?.id}    
                />
            </ModalWrapper> 
        </div>
    );
};

export default Archive;